import React from "react";
import PreferenceWizard from "./meal-planner/PreferenceWizard";
import { useUserPreferences } from "@/lib/hooks/useUserPreferences";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";
import { translations } from "@/lib/translations";
import { ProfileMenu } from "./ProfileMenu";
import { useAuth } from "@/lib/auth";
import { generateMealPlan, getUserMealPlans } from "@/lib/mealPlan";
import { useNavigate } from "react-router-dom";

const loadingMessages = [
  "מחפש את המתכונים הכי טעימים... 😋",
  "מערבב את הקדירה הקסומה... 🪄",
  "מתייעץ עם השפים הכי טובים... 👨‍🍳",
  "מחפש את המצרכים הטריים ביותר... 🥬",
  "מנסה לא לאכול את כל האוכל בדרך... 😅",
  "מתווכח עם הסיר על טמפרטורת הבישול... 🍲",
  "מלמד את התנור לרקוד... 💃",
  "סופר גרגירי אורז... 🍚",
  "מחבק עגבניות... 🍅",
  "מספר בדיחות לירקות... 🥕",
];

export default function Home() {
  const { preferences, loading, error, saveUserPreferences } =
    useUserPreferences();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [generating, setGenerating] = React.useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = React.useState(0);

  React.useEffect(() => {
    if (generating) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [generating]);

  const handlePreferencesComplete = async (newPreferences: any) => {
    try {
      setGenerating(true);
      await saveUserPreferences(newPreferences);
      await generateMealPlan(newPreferences);

      // Get the latest plans to find the ID of the newly created plan
      const plans = await getUserMealPlans();
      const latestPlan = plans[0]; // Plans are ordered by created_at desc
      navigate(`/meal-plan/${latestPlan.id}`);
    } catch (err) {
      console.error("Failed to save preferences or generate meal plan:", err);
    } finally {
      setGenerating(false);
    }
  };

  if (loading || generating) {
    return (
      <div className="min-h-screen w-full bg-gray-50 p-8 flex flex-col items-center justify-center gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-lg text-muted-foreground animate-pulse">
          {loadingMessages[loadingMessageIndex]}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50">
      {/* Header with Profile Menu */}
      <div className="w-full bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img
              src="/PlateMate_logo.svg"
              alt="PlateMate"
              className="h-10 w-auto"
            />
            <h1 className="text-xl font-semibold">
              {translations.mealPlanner.title}
            </h1>
          </div>
          {user && <ProfileMenu user={user} />}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="text-center space-y-2">
          <p className="text-lg text-muted-foreground">
            {translations.mealPlanner.subtitle}
          </p>
        </div>

        <div className="mt-8">
          <PreferenceWizard
            initialData={preferences || undefined}
            onComplete={handlePreferencesComplete}
          />
        </div>
      </div>
    </div>
  );
}
